import React from 'react';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { SEO } from '../components/seo/SEO';
import { JsonLd } from '../components/seo/JsonLd';
import { createBreadcrumbSchema } from '../lib/structured-data';
import { Shield, Server, CreditCard, Mail, Globe } from 'lucide-react';

const subprocessorData = [
  {
    name: 'Supabase Inc.',
    role: 'Database, Authentication & File Storage',
    location: 'AWS ca-central-1, Montreal, Quebec, Canada',
    dataTypes: 'Account information (email, name), chat messages, uploaded documents, session metadata',
    website: 'https://supabase.com',
    lastUpdated: '2026-03-19',
  },
  {
    name: 'Cloudflare Inc.',
    role: 'CDN, Edge Routing, DDoS Protection & SSL Termination',
    location: 'Canadian edge nodes (transit only; data not stored)',
    dataTypes: 'HTTP request/response headers, IP addresses (transient, not persisted)',
    website: 'https://cloudflare.com',
    lastUpdated: '2026-03-19',
  },
  {
    name: 'Nebula Block Inc.',
    role: 'AI Inference (Large Language Model API)',
    location: 'Montreal, Quebec, Canada',
    dataTypes: 'Chat prompts and document text (processed for inference only; not stored after request completes)',
    website: 'https://nebulablock.com',
    lastUpdated: '2026-03-19',
  },
  {
    name: 'Stripe Inc.',
    role: 'Payment Processing',
    location: 'United States (with Canadian data residency option for Enterprise)',
    dataTypes: 'Billing name, billing email, payment method tokens (card data handled by Stripe directly)',
    website: 'https://stripe.com',
    lastUpdated: '2026-04-17',
  },
  {
    name: 'Brevo (Sendinblue SAS)',
    role: 'Transactional Email Delivery',
    location: 'Paris, France, European Union',
    dataTypes: 'Recipient email address and name (for account-related emails only; no client tax data)',
    website: 'https://brevo.com',
    lastUpdated: '2026-04-17',
  },
];

export function Subprocessors() {
  return (
    <div className="min-h-screen flex flex-col bg-[var(--color-csai-bg)]">
      <Header />

      <main className="flex-grow">
        <SEO
          title="Sub-processors"
          description="Third-party service providers that process personal data on behalf of Situs AI. All data stored in Canada except Stripe (payments) and Brevo (email)."
          canonical="https://situsai.ca/subprocessors"
        />
        <JsonLd data={createBreadcrumbSchema('/subprocessors')} />

        {/* Hero */}
        <section className="py-20 sm:py-28 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[var(--color-csai-surface)] text-[var(--color-csai-primary)] text-sm font-medium mb-8">
            <Shield className="w-4 h-4" /> Data Processing Transparency
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 text-[var(--color-csai-accent)] tracking-tight">
            Our Sub-processors
          </h1>
          <p className="text-xl text-[var(--color-csai-muted)] max-w-3xl mx-auto">
            We use trusted third-party service providers to deliver the Orca AI platform. All sub-processors are bound by data protection obligations equivalent to our own.
          </p>
        </section>

        {/* Notice */}
        <section className="pb-16 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-6 text-amber-900 text-sm max-w-4xl mx-auto">
            <strong>Enterprise customers:</strong> If you have a Data Processing Agreement that requires advance notice of sub-processor changes, we will notify you at least 30 days before any new sub-processor is added. Contact <a href="mailto:info@situsai.ca" className="underline">info@situsai.ca</a> for more information.
          </div>
        </section>

        {/* Sub-processors Table */}
        <section className="pb-24 px-4 sm:px-6 lg:px-8 max-w-5xl mx-auto">
          <div className="space-y-6">
            {subprocessorData.map((sp) => (
              <div key={sp.name} className="bg-white rounded-2xl border border-[var(--color-csai-surface)] p-8 shadow-sm">
                <div className="flex flex-col sm:flex-row sm:items-start gap-4 mb-6">
                  <div className="flex-1">
                    <h2 className="text-2xl font-bold text-[var(--color-csai-accent)] mb-1">{sp.name}</h2>
                    <p className="text-[var(--color-csai-primary)] font-medium">{sp.role}</p>
                  </div>
                  <a
                    href={sp.website}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-sm text-[var(--color-csai-muted)] hover:text-[var(--color-csai-primary)] transition-colors"
                  >
                    <Globe className="w-4 h-4" /> Website
                  </a>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-[var(--color-csai-muted)] text-xs uppercase tracking-wider mb-1">Processing Location</p>
                    <p className="text-[var(--color-csai-accent)] font-medium">{sp.location}</p>
                  </div>
                  <div>
                    <p className="text-[var(--color-csai-muted)] text-xs uppercase tracking-wider mb-1">Data Types Processed</p>
                    <p className="text-[var(--color-csai-accent)]">{sp.dataTypes}</p>
                  </div>
                  <div>
                    <p className="text-[var(--color-csai-muted)] text-xs uppercase tracking-wider mb-1">Last Updated</p>
                    <p className="text-[var(--color-csai-accent)]">{sp.lastUpdated}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Exceptions Note */}
          <div className="mt-8 bg-[var(--color-csai-surface)] rounded-xl p-6 text-sm text-[var(--color-csai-muted)]">
            <p><strong>Note on data residency:</strong> Core platform data (chat messages, documents, account information) is stored exclusively in Canada. The exceptions above are:</p>
            <ul className="mt-2 space-y-1 list-disc list-inside">
              <li><strong>Stripe</strong> — processes payment method tokens; card data never touches our servers. Stripe offers Canadian data residency for Enterprise accounts.</li>
              <li><strong>Brevo</strong> — processes email addresses for transactional account emails only. No client tax data or chat content is sent to Brevo.</li>
            </ul>
          </div>
        </section>

        {/* Back */}
        <section className="pb-16 text-center px-4">
          <a href="/privacy" className="text-[var(--color-csai-primary)] hover:underline text-sm">
            &larr; Back to Privacy Policy
          </a>
        </section>
      </main>

      <Footer />
    </div>
  );
}
